import type { Attendance, Person } from "../types";

type CheckRow = Attendance & { name: string };

const numberValue = (value: unknown) => Number(value || 0);

export function attendanceCheckProblems(attendance: Attendance[], people: Person[], personId = "") {
  const names = new Map(people.map(person => [person.id, person.name]));
  const rows = personId ? attendance.filter(row => row.person_id === personId) : attendance;
  const seen = new Map<string, number>();
  const dailyRegular = new Map<string, number>();
  const problems: string[] = [];
  rows.forEach(row => {
    const name = names.get(row.person_id) || "未建档人员";
    const key = `${row.person_id}|${row.work_date}|${String(row.area || "").trim()}`;
    seen.set(key, (seen.get(key) || 0) + 1);
    const regular = numberValue(row.regular_hours);
    const overtime = numberValue(row.overtime_hours);
    const dailyKey = `${row.person_id}|${row.work_date}`;
    dailyRegular.set(dailyKey, (dailyRegular.get(dailyKey) || 0) + regular);
    if (!names.has(row.person_id)) problems.push(`${name} ${row.work_date}：人员资料中未找到该人员`);
    if (!Number.isFinite(regular) || regular < 0 || regular > 8) problems.push(`${name} ${row.work_date}：正班${regular}小时，必须在0～8小时之间`);
    if (!Number.isFinite(overtime) || overtime < 0 || overtime > 24) problems.push(`${name} ${row.work_date}：加班${overtime}小时，必须在0～24小时之间`);
  });
  seen.forEach((count, key) => {
    if (count <= 1) return;
    const [id, date, area] = key.split("|");
    problems.push(`${names.get(id) || "未建档人员"} ${date} ${area || "未分区域"}：存在${count}条同区域重复考勤`);
  });
  dailyRegular.forEach((total, key) => {
    if (total <= 8.0001) return;
    const [id, date] = key.split("|");
    problems.push(`${names.get(id) || "未建档人员"} ${date}：各区域正班合计${total}小时，超过8小时；加班不计入`);
  });
  return problems;
}

function safeSheetName(value: string) {
  return (value || "考勤核对").replace(/[\\/*?:[\]]/g, "_").slice(0, 31) || "考勤核对";
}

function attendanceQuantity(hours: number) {
  const value = numberValue(hours) / 8;
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

function localTimestamp() {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

export async function buildAttendanceCheckXlsx(projectName: string, month: string, attendance: Attendance[], people: Person[], personId = "") {
  const ExcelJS = (await import("exceljs")).default;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "工时记";
  workbook.created = new Date();
  const nameMap = new Map(people.map(person => [person.id, person.name]));
  const selectedRows: CheckRow[] = attendance
    .filter(row => !personId || row.person_id === personId)
    .map(row => ({ ...row, name: nameMap.get(row.person_id) || "未建档人员" }))
    .sort((a, b) => a.name.localeCompare(b.name, "zh-CN") || a.work_date.localeCompare(b.work_date) || (a.area || "").localeCompare(b.area || "", "zh-CN"));
  if (!selectedRows.length) throw new Error("当前选择没有可导出的考勤记录");

  const grouped = new Map<string, CheckRow[]>();
  selectedRows.forEach(row => grouped.set(row.name, [...(grouped.get(row.name) || []), row]));
  const usedNames = new Set<string>();
  const [year, monthNumber] = month.split("-").map(Number);
  const border = {
    top: { style: "thin" as const, color: { argb: "FF999999" } },
    left: { style: "thin" as const, color: { argb: "FF999999" } },
    bottom: { style: "thin" as const, color: { argb: "FF999999" } },
    right: { style: "thin" as const, color: { argb: "FF999999" } },
  };

  for (const [name, rows] of grouped) {
    let sheetName = safeSheetName(name);
    let index = 2;
    while (usedNames.has(sheetName)) {
      const suffix = String(index++);
      sheetName = `${safeSheetName(name).slice(0, 31 - suffix.length)}${suffix}`;
    }
    usedNames.add(sheetName);
    const sheet = workbook.addWorksheet(sheetName, { views: [{ state: "frozen", ySplit: 3, topLeftCell: "A4", activeCell: "A4" }] });
    sheet.columns = [8, 14, 14, 12, 12, 12, 38].map(width => ({ width }));
    sheet.mergeCells("A1:G1");
    sheet.getCell("A1").value = `${projectName || "全部项目"} ${year}年${monthNumber}月考勤核对明细（${name}）`;
    sheet.getCell("A1").font = { name: "微软雅黑", size: 14, bold: true };
    sheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(1).height = 26;
    sheet.getCell("A2").value = `导出时间：${localTimestamp()}`;
    sheet.getCell("E2").value = "说明：考勤数量按正班小时÷8换算；加班小时单独列示，不计入考勤数量。";
    const headers = ["序号", "姓名", "日期", "考勤数量", "正班小时", "加班小时", "所在区域"];
    sheet.getRow(3).values = headers;
    sheet.getRow(3).eachCell(cell => {
      cell.font = { name: "微软雅黑", bold: true };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9EAF7" } };
      cell.border = border;
    });
    rows.forEach((row, rowIndex) => {
      const excelRow = sheet.addRow([rowIndex + 1, row.name, row.work_date, attendanceQuantity(numberValue(row.regular_hours)), numberValue(row.regular_hours), numberValue(row.overtime_hours), row.area || "未分区域考勤"]);
      excelRow.eachCell((cell, column) => {
        cell.alignment = { horizontal: column === 7 ? "left" : "center", vertical: "middle" };
        cell.border = border;
      });
    });
    const totalRow = sheet.addRow(["合计", null, null, Number((rows.reduce((sum, row) => sum + numberValue(row.regular_hours), 0) / 8).toFixed(2)), rows.reduce((sum, row) => sum + numberValue(row.regular_hours), 0), rows.reduce((sum, row) => sum + numberValue(row.overtime_hours), 0), null]);
    totalRow.eachCell({ includeEmpty: true }, cell => {
      cell.font = { name: "微软雅黑", bold: true };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2F0D9" } };
      cell.border = border;
    });
    sheet.autoFilter = { from: "A3", to: `G${totalRow.number}` };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const selectedName = personId ? nameMap.get(personId) || "指定人员" : "全部人员";
  const filename = `${year}年${monthNumber}月${projectName || "项目"}考勤核对明细_${selectedName}.xlsx`;
  return { bytes: new Uint8Array(buffer), filename: filename.replace(/[\\/:*?"<>|]/g, "_"), count: selectedRows.length };
}

export async function exportAttendanceCheckXlsx(projectName: string, month: string, attendance: Attendance[], people: Person[], personId = "") {
  const result = await buildAttendanceCheckXlsx(projectName, month, attendance, people, personId);
  const blob = new Blob([result.bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = result.filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return result.count;
}
